import { Platform } from "react-native"

// L'identité d'un lead dans l'URL d'un funnel d'acquisition (`?user=` = e-mail
// en base64) : la lire, la propager, l'écrire, la retirer, et reconnaître
// qu'elle désigne un autre lead que celui en mémoire. Une seule écriture pour
// les gardes des funnels stage et inscription, qui avaient divergé.

const IS_WEB = Platform.OS === "web"

/**
 * Lit le param `user` (base64 email) — d'abord depuis les params de route
 * (propagés via navigation.navigate), sinon depuis l'URL web (?user=…). C'est
 * l'identité du lead, présente sur le lien magique d'entrée et propagée sur
 * chaque étape pour rendre le funnel partageable par URL. Partagé par les
 * gardes des funnels stage et inscription.
 */
export const readUserParam = (route) =>
    route?.params?.user ??
    (IS_WEB && typeof window !== "undefined"
        ? new URLSearchParams(window.location?.search || "").get("user")
        : null)

/**
 * Params à propager sur chaque `navigation.navigate` aval pour garder l'URL
 * partageable (`?user=…`). `undefined` quand il n'y a pas de `user`.
 */
export const userNavParams = (userParam) =>
    userParam ? { user: userParam } : undefined

/**
 * Écrit `?user=…` dans l'URL web courante (sans recharger ni empiler dans
 * l'historique) pour que l'URL de l'étape soit partageable telle quelle.
 */
export const ensureUserInUrl = (userParam) => {
    if (!IS_WEB || typeof window === "undefined") return
    try {
        const url = new URL(window.location.href)
        if (url.searchParams.get("user") === userParam) return
        url.searchParams.set("user", userParam)
        window.history.replaceState(window.history.state, "", url.toString())
    } catch (_e) {
        /* noop — replaceState indisponible (très vieux navigateur) */
    }
}

/**
 * Retire `?user=…` de l'URL web courante. Symétrique de {@link ensureUserInUrl},
 * pour les sorties volontaires du parcours repris (« Reprendre depuis le
 * début ») : sans ce nettoyage, la garde relit le jeton dans l'URL avant que
 * la navigation ne la réécrive, et renvoie vers la reprise qu'on vient de
 * quitter.
 */
export const clearUserFromUrl = () => {
    if (!IS_WEB || typeof window === "undefined") return
    try {
        const url = new URL(window.location.href)
        if (!url.searchParams.has("user")) return
        url.searchParams.delete("user")
        window.history.replaceState(window.history.state, "", url.toString())
    } catch (_e) {
        /* noop — replaceState indisponible (très vieux navigateur) */
    }
}

/** Email porté par le jeton `?user=`, ou null s'il est illisible. */
const decodeUserParam = (userParam) => {
    if (!userParam || typeof atob !== "function") return null
    try {
        return atob(userParam)
    } catch (_e) {
        return null
    }
}

/**
 * L'URL désigne-t-elle un AUTRE lead que celui en mémoire ?
 *
 * Cas réel : un admin enchaîne plusieurs familles dans le même onglet. Le
 * contexte n'est pas froid — il porte encore le lead précédent — donc la garde
 * ne se déclenchait pas, et l'écran affichait le parcours de la famille d'avant
 * sous l'URL de la nouvelle. Un jeton `?user=` différent est un changement de
 * lead explicite : il doit repasser par la reprise, qui remet à zéro avant de
 * réhydrater.
 *
 * Comparaison insensible à la casse : les emails ne le sont pas, et un jeton
 * fabriqué à la main ne doit pas provoquer une reprise en boucle.
 */
export const isOtherLead = (state, userParam) => {
    const email = decodeUserParam(userParam)
    if (!email || !state?.parentEmail) return false

    return (
        email.trim().toLowerCase() !==
        String(state.parentEmail).trim().toLowerCase()
    )
}

/**
 * Un lien de reprise que le serveur ne reconnaîtra jamais (identité absente,
 * refusée ou inconnue) : « Réessayer » n'y peut rien, contrairement à une panne
 * passagère.
 */
export const isUnreadableResumeLink = (userParam, error) =>
    !userParam || [401, 403, 404].includes(error?.status)
